import {
  InactivityTimeoutSettings,
  InactivityTimeoutSettingsEndpoint,
  SetInactivityTimeoutSettingsEndpoint,
  ShareSnippet,
} from '@fullstory/client-api';
import {
  InviteUmbrellaUserRequest,
  InviteUsersRequest,
  InviteUsersResponse,
  SettingsResponse,
  TeamSettings,
  UpdateAjaxAllowList,
  UpdateDiscardIpAddressesRequest,
  UpdateElementBlocksRequest,
} from '@fullstory/proto/fs/api/clientapi';
import {
  AnalyzeQuotaRequest,
  ApiKeyMetadata,
  AuthPublic,
  BatchDeleteSeatsRequest,
  BatchRevokeInvitesRequest,
  BatchUpdateOrgSeatsRequest,
  DeleteApiKeyRequest,
  GenerateUserApiKeyRequest,
  GetApiKeysRequest,
  GetApiKeysResponse,
  GetSeatsAndInvitesRequest,
  Invite,
  OrgID,
  OrgOrUmbrellaID,
  OrgsServicePublic,
  PrivacySettingsResponse,
  Role,
  UpdateCustomSeatRequestConfigRequest,
  UpdateJoinSettingsRequest,
  UpdateNotificationConfigRequest,
  UpdateSeatDisabledRequest,
  UpdateSessionOverageConfigRequest,
  UpdateUserApiKeyRequest,
} from '@fullstory/proto/fs/services/orgs';
import {
  SeatSettingsPublic,
  UpdateDismissibleWarningRequest,
} from '@fullstory/proto/fs/services/webber';
import { timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import {
  ActionType,
  createAction,
  createGRPCAction,
  createRequestAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { RequestType } from '@fullstory/util/xhr';
import { SeatSettings, SettingsTypes } from './types';

const SETTINGS_ENDPOINT = '/settings';
const UPDATE_SHARE_ENABLED_ENDPOINT = '/settings/updateShareEnabled';
const UPDATE_SHARE_DOMAINS_ENDPOINT = '/settings/updateShareDomains';
const CHANGE_PASSWORD_ENDPOINT = '/passwordresetrequest';
const TOGGLE_GOOGLE_AUTH_ENDPOINT = '/setGoogleAuthEnforced';
const TOGGLE_SSO_AUTH_ENDPOINT = '/setSSOAuthEnforced';
const UPDATE_SEAT_SETTINGS_ENDPOINT = '/updateSeatSettings';
const SEND_SNIPPET_ENDPOINT = '/sendSnippet';

export const TEAM_SETTINGS_ENDPOINT = '/settings/team';
export const INVITE_ENDPOINT = '/settings/sendInvite';
export const INVITES_ENDPOINT = '/settings/sendInvites';
export const PRIVACY_SETTINGS_ENDPOINT = '/settings/privacy';
export const UPDATE_DISCARD_IP_ADDRS_ENDPOINT = '/settings/updateDiscardIpAddresses';
export const REVOKE_INVITE_ENDPOINT = '/revokeInvite';
export const UPDATE_SEAT_ENDPOINT = '/updateSeat';
export const UPDATE_AJAX_ALLOW_ENDPOINT = '/settings/updateAjaxAllowList';
export const UPDATE_BLOCKS_ENDPOINT = '/settings/updateBlocks';
export const UPDATE_SEATS = '/updateSeats';
export const WEB_RECORDING_SETTINGS_ENDPOINT = '/settings/webRecording';
export const UPDATE_WEB_RECORDING_SETTINGS_ENDPOINT = '/settings/updateWebRecording';
export const SEND_PRIVACY_SETUP_EMAIL_ENDPOINT = '/sendPrivacySetupEmail';
export const SEND_UMBRELLA_INVITE_ENDPOINT = '/settings/sendUmbrellaInvite';

export const SettingsRequestActions = {
  getSettings: () =>
    createRequestAction<SettingsResponse>()(
      {
        type: SettingsTypes.FetchSettingsRequest,
        buildRequest: {
          requestType: RequestType.GetJson,
          url: SETTINGS_ENDPOINT,
        },
        readWrite: 'write',
      },
      SettingsTypes.FetchSettingsSuccess,
      SettingsTypes.FetchSettingsFailure,
    ),
  getTeamSettings: () =>
    createRequestAction<TeamSettings>()(
      {
        type: SettingsTypes.FetchTeamSettingsRequest,
        buildRequest: {
          requestType: RequestType.GetJson,
          url: TEAM_SETTINGS_ENDPOINT,
        },
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.FetchTeamSettingsSuccess,
      SettingsTypes.FetchTeamSettingsFailure,
    ),
  getSeatsInvites: (
    entityId: OrgOrUmbrellaID,
    filters: GetSeatsAndInvitesRequest.Filter[] = [],
    includeNames: boolean = false,
    force: boolean = true,
    includeGroups: boolean = false,
  ) => {
    const body: GetSeatsAndInvitesRequest = {
      entityId: {},
      filters,
      includeNames,
      includeGroups,
    };

    // Do not want to send the server an empty strings for ids or it will return a 400
    if (body.entityId) {
      if (entityId.orgId) {
        body.entityId.orgId = entityId.orgId;
      }
      if (entityId.umbrellaId) {
        body.entityId.umbrellaId = entityId.umbrellaId;
      }
    }

    return createGRPCAction<typeof OrgsServicePublic.GetSeatsAndInvites>()(
      {
        type: SettingsTypes.FetchSeatsInvitesRequest,
        grpcFunction: OrgsServicePublic.GetSeatsAndInvites,
        readWrite: force ? 'write' : 'read',
        force,
        body,
      },
      SettingsTypes.FetchSeatsInvitesSuccess,
      SettingsTypes.FetchSeatsInvitesFailure,
    );
  },
  getGuests: (
    entityId: OrgOrUmbrellaID,
    filters: GetSeatsAndInvitesRequest.Filter[] = [],
    includeNames: boolean = false,
    force: boolean = true,
  ) => {
    // Always filter for Guest role
    filters.push({
      rolesFilter: {
        roles: [Role.VIEW_ONLY],
      },
    });
    const body: GetSeatsAndInvitesRequest = {
      entityId: {},
      filters,
      includeNames,
      includeGroups: false,
    };

    // Do not want to send the server an empty strings for ids or it will return a 400
    if (body.entityId) {
      if (entityId.orgId) {
        body.entityId.orgId = entityId.orgId;
      }
      if (entityId.umbrellaId) {
        body.entityId.umbrellaId = entityId.umbrellaId;
      }
    }

    return createGRPCAction<typeof OrgsServicePublic.GetSeatsAndInvites>()(
      {
        type: SettingsTypes.FetchGuestsRequest,
        grpcFunction: OrgsServicePublic.GetSeatsAndInvites,
        readWrite: force ? 'write' : 'read',
        force,
        body,
      },
      SettingsTypes.FetchGuestsSuccess,
      SettingsTypes.FetchGuestsFailure,
    );
  },
  updateShareEnabled: (sharingEnabled: boolean) =>
    createRequestAction<{ status: string }>()(
      {
        type: SettingsTypes.UpdateShareEnabledRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_SHARE_ENABLED_ENDPOINT,
        },
        body: sharingEnabled,
        readWrite: 'write',
      },
      SettingsTypes.UpdateShareEnabledSuccess,
      SettingsTypes.UpdateShareEnabledFailure,
    ),
  updateShareDomains: (orgId: string, domain: string, action: 'add' | 'remove') =>
    createRequestAction<string[]>()(
      {
        type: SettingsTypes.UpdateDomainsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_SHARE_DOMAINS_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          orgId,
          updates:
            action === 'add' ? { adds: [domain], deletes: [] } : { adds: [], deletes: [domain] },
        },
      },
      SettingsTypes.UpdateDomainsSuccess,
      SettingsTypes.UpdateDomainsFailure,
    ),
  updateJoinOrgSettings: (body: UpdateJoinSettingsRequest) =>
    createGRPCAction<typeof OrgsServicePublic.UpdateJoinOrgSettings>()(
      {
        type: SettingsTypes.UpdateJoinOrgSettingsRequest,
        grpcFunction: OrgsServicePublic.UpdateJoinOrgSettings,
        body,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.UpdateJoinOrgSettingsSuccess,
      SettingsTypes.UpdateJoinOrgSettingsFailure,
    ),
  passwordReset: (email: string) =>
    createRequestAction()(
      {
        type: SettingsTypes.PasswordResetRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: CHANGE_PASSWORD_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          Username: email,
        },
      },
      SettingsTypes.PasswordResetSuccess,
      SettingsTypes.PasswordResetFailure,
    ),
  updateGoogleAuth: (enforced: boolean) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateGoogleAuthRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: TOGGLE_GOOGLE_AUTH_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          Enforced: enforced,
        },
        force: true,
      },
      SettingsTypes.UpdateGoogleAuthSuccess,
      SettingsTypes.UpdateGoogleAuthFailure,
    ),
  updateSSOAuth: (enforced: boolean) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateSSOAuthRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: TOGGLE_SSO_AUTH_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          Enforced: enforced,
        },
        force: true,
      },
      SettingsTypes.UpdateSSOAuthSuccess,
      SettingsTypes.UpdateSSOAuthFailure,
    ),
  sendInvite: (email: string, role: number) =>
    createRequestAction<Invite>()(
      {
        type: SettingsTypes.SendInviteRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: INVITE_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          email,
          role,
        },
        force: true,
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.SendInviteSuccess,
      SettingsTypes.SendInviteFailure,
    ),
  sendInvites: (req: InviteUsersRequest) =>
    createRequestAction<InviteUsersResponse>()(
      {
        type: SettingsTypes.SendInvitesRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: INVITES_ENDPOINT,
        },
        readWrite: 'write',
        body: req,
        force: true,
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.SendInvitesSuccess,
      SettingsTypes.SendInvitesFailure,
    ),
  sendSnippet: (email: string, message: string, invite: boolean) =>
    createRequestAction<ShareSnippet>()(
      {
        type: SettingsTypes.SendSnippetRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: SEND_SNIPPET_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          EmailTo: email,
          Message: message,
          Invite: invite,
        },
        force: true,
      },
      SettingsTypes.SendSnippetSuccess,
      SettingsTypes.SendSnippetFailure,
    ),
  sendPrivacySetupEmail: () =>
    createRequestAction<{}>()(
      {
        type: SettingsTypes.SendPrivacySetupEmailRequest,
        buildRequest: {
          requestType: RequestType.Post,
          url: SEND_PRIVACY_SETUP_EMAIL_ENDPOINT,
        },
        readWrite: 'write',
        force: true,
      },
      SettingsTypes.SendPrivacySetupEmailSuccess,
      SettingsTypes.SendPrivacySetupEmailFailure,
    ),
  sendUmbrellaInvite: (req: InviteUmbrellaUserRequest) =>
    createRequestAction<void>()(
      {
        type: SettingsTypes.SendUmbrellaInviteRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: SEND_UMBRELLA_INVITE_ENDPOINT,
        },
        readWrite: 'write',
        body: req,
        force: true,
      },
      SettingsTypes.SendUmbrellaInviteSuccess,
      SettingsTypes.SendUmbrellaInviteFailure,
    ),
  revokeInvite: (email: string) =>
    createRequestAction<{ status: string }, string>({ modify: (_): string => email })(
      {
        type: SettingsTypes.RevokeInviteRequest,
        buildRequest: {
          requestType: RequestType.GetJson,
          url: REVOKE_INVITE_ENDPOINT,
          params: { Email: email },
        },
        readWrite: 'write',
        force: true,
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.RevokeInviteSuccess,
      SettingsTypes.RevokeInviteFailure,
    ),
  updateSeat: (email: string, role?: Role) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateSeatRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_SEAT_ENDPOINT,
        },
        readWrite: 'write',
        body: {
          Email: email,
          SeatRole: role,
          Disable: role === undefined, // This is a noop unless set to true
        },
        force: true,
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.UpdateSeatSuccess,
      SettingsTypes.UpdateSeatFailure,
    ),
  getInactivityTimeoutSettings: () =>
    createRequestAction<InactivityTimeoutSettings>()(
      {
        type: SettingsTypes.FetchInactivityTimeoutSettingsRequest,
        readWrite: 'write',
        buildRequest: {
          requestType: RequestType.GetJson,
          url: InactivityTimeoutSettingsEndpoint,
        },
      },
      SettingsTypes.FetchInactivityTimeoutSettingsSuccess,
      SettingsTypes.FetchInactivityTimeoutSettingsFailure,
    ),
  setInactivityTimeoutSettings: (UserInactivityTimeout: number, AdminInactivityTimeout: number) =>
    createRequestAction<InactivityTimeoutSettings>()(
      {
        type: SettingsTypes.UpdateInactivityTimeoutSettingsRequest,
        readWrite: 'write',
        buildRequest: {
          requestType: RequestType.PostJson,
          url: SetInactivityTimeoutSettingsEndpoint,
        },
        body: {
          UserInactivityTimeout,
          AdminInactivityTimeout,
        },
        force: true,
      },
      SettingsTypes.UpdateInactivityTimeoutSettingsSuccess,
      SettingsTypes.UpdateInactivityTimeoutSettingsFailure,
    ),
  updateSeatSettings: (req: SeatSettings) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateSeatSettingsStarted,
        readWrite: 'write',
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_SEAT_SETTINGS_ENDPOINT,
        },
        body: req,
        force: true,
      },
      SettingsTypes.UpdateSeatSettingsSuccess,
      SettingsTypes.UpdateSeatSettingsFailure,
    ),
  getApiKeys: (orgId: string) => {
    const body: GetApiKeysRequest = {
      orgId,
    };
    return createGRPCAction<typeof AuthPublic.GetApiKeys>({
      cacheKey: () => getApiKeysHashKey(orgId),
    })(
      {
        type: SettingsTypes.FetchApiKeysRequest,
        grpcFunction: AuthPublic.GetApiKeys,
        readWrite: 'write',
        body,
      },
      SettingsTypes.FetchApiKeysSuccess,
      SettingsTypes.FetchApiKeysFailure,
    );
  },
  deleteApiKey: (req: DeleteApiKeyRequest) =>
    createGRPCAction<typeof AuthPublic.DeleteApiKey>()(
      {
        type: SettingsTypes.DeleteApiKeyRequest,
        grpcFunction: AuthPublic.DeleteApiKey,
        body: req,
        invalidateCacheKey: getApiKeysHashKey(req.orgId),
        readWrite: 'write',
        force: true,
      },
      SettingsTypes.DeleteApiKeySuccess,
      SettingsTypes.DeleteApiKeyFailure,
    ),
  createApiKey: (req: GenerateUserApiKeyRequest) =>
    createGRPCAction<
      typeof AuthPublic.GenerateUserApiKey,
      { meta: ApiKeyMetadata; apiKey: string }
    >({
      modify: (data): { meta: ApiKeyMetadata; apiKey: string } => ({
        meta: {
          name: req.name,
          seatId: req.seatId,
          role: req.orgProperties!.role,
          flags: req.scopedProperties?.flags ?? [],
          suffix: data.suffix,
          generated: Timestamp.now(),
        },
        apiKey: data.apiKey,
      }),
    })(
      {
        type: SettingsTypes.CreateApiKeyRequest,
        grpcFunction: AuthPublic.GenerateUserApiKey,
        body: req,
        invalidateCacheKey: getApiKeysHashKey(req.orgProperties!.orgId),
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.CreateApiKeySuccess,
      SettingsTypes.CreateApiKeyFailure,
    ),
  updateApiKey: (req: UpdateUserApiKeyRequest) =>
    createGRPCAction<typeof AuthPublic.UpdateUserApiKey>()(
      {
        type: SettingsTypes.UpdateApiKeyRequest,
        grpcFunction: AuthPublic.UpdateUserApiKey,
        body: req,
        invalidateCacheKey: getApiKeysHashKey(req.orgId),
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.UpdateApiKeySuccess,
      SettingsTypes.UpdateApiKeyFailure,
    ),
  getPrivacySettings: () =>
    createRequestAction<PrivacySettingsResponse>()(
      {
        type: SettingsTypes.FetchPrivacySettingsRequest,
        buildRequest: {
          requestType: RequestType.GetJson,
          url: PRIVACY_SETTINGS_ENDPOINT,
        },
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.FetchPrivacySettingsSuccess,
      SettingsTypes.FetchPrivacySettingsFailure,
    ),
  updateDiscardIpAddress: (body: UpdateDiscardIpAddressesRequest) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateDiscardIpAddrsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_DISCARD_IP_ADDRS_ENDPOINT,
        },
        body,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.UpdateDiscardIpAddrsSuccess,
      SettingsTypes.UpdateDiscardIpAddrsFailure,
    ),
  sendSnippetEmail: (shareSnippet: ShareSnippet) =>
    createRequestAction()(
      {
        type: SettingsTypes.SendSnippetEmailRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: '/sendSnippet',
        },
        body: shareSnippet,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.SendSnippetEmailSuccess,
      SettingsTypes.SendSnippetEmailFailure,
    ),
  updateAjaxAllowlistedReq: (req: UpdateAjaxAllowList) =>
    createRequestAction()(
      {
        type: SettingsTypes.UpdateAjaxAllowlistedReqRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_AJAX_ALLOW_ENDPOINT,
        },
        body: req,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.UpdateAjaxAllowlistedReqSuccess,
      SettingsTypes.UpdateAjaxAllowlistedReqFailure,
    ),
  updateElementRecordingRules: (body: UpdateElementBlocksRequest) =>
    createRequestAction<{ updateTime: timestamp; updatedSelectors: string[] }>()(
      {
        type: SettingsTypes.UpdateElementRecordingRulesRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_BLOCKS_ENDPOINT,
        },
        body,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.UpdateElementRecordingRulesSuccess,
      SettingsTypes.UpdateElementRecordingRulesFailure,
    ),
  updateSeatDisabled: (body: UpdateSeatDisabledRequest) =>
    createGRPCAction<typeof OrgsServicePublic.UpdateSeatDisabled>()(
      {
        type: SettingsTypes.UpdateSeatDisabledRequest,
        grpcFunction: OrgsServicePublic.UpdateSeatDisabled,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.UpdateSeatDisabledSuccess,
      SettingsTypes.UpdateSeatDisabledFailure,
    ),
  batchDeleteSeats: (body: BatchDeleteSeatsRequest) =>
    createGRPCAction<typeof OrgsServicePublic.BatchDeleteSeats>()(
      {
        type: SettingsTypes.BatchDeleteSeatsRequest,
        grpcFunction: OrgsServicePublic.BatchDeleteSeats,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.BatchDeleteSeatsSuccess,
      SettingsTypes.BatchDeleteSeatsFailure,
    ),
  batchRevokeInvites: (body: BatchRevokeInvitesRequest) =>
    createGRPCAction<typeof OrgsServicePublic.BatchRevokeInvites>()(
      {
        type: SettingsTypes.BatchRevokeInvitesRequest,
        grpcFunction: OrgsServicePublic.BatchRevokeInvites,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: [defaultCacheKey(SETTINGS_ENDPOINT, undefined)],
      },
      SettingsTypes.BatchRevokeInvitesSuccess,
      SettingsTypes.BatchRevokeInvitesFailure,
    ),
  batchUpdateSeats: (body: BatchUpdateOrgSeatsRequest) =>
    createRequestAction<BatchUpdateOrgSeatsRequest.Update>()(
      {
        type: SettingsTypes.BatchUpdateSeatsRequest,
        buildRequest: {
          requestType: RequestType.PostJson,
          url: UPDATE_SEATS,
        },
        body,
        force: true,
        readWrite: 'write',
      },
      SettingsTypes.BatchUpdateSeatsSuccess,
      SettingsTypes.BatchUpdateSeatsFailure,
    ),
  analyzeQuota: (orgId: string) => {
    const body: AnalyzeQuotaRequest = {
      orgId,
    };
    return createGRPCAction<typeof OrgsServicePublic.AnalyzeQuota>()(
      {
        type: SettingsTypes.AnalyzeQuotaRequest,
        grpcFunction: OrgsServicePublic.AnalyzeQuota,
        readWrite: 'write',
        body,
        force: true,
      },
      SettingsTypes.AnalyzeQuotaSuccess,
      SettingsTypes.AnalyzeQuotaFailure,
    );
  },
  getSessionOverageConfig: (body: OrgOrUmbrellaID) =>
    createGRPCAction<typeof OrgsServicePublic.GetSessionOverageConfig>()(
      {
        type: SettingsTypes.GetSessionOverageConfigRequest,
        grpcFunction: OrgsServicePublic.GetSessionOverageConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.GetSessionOverageConfigSuccess,
      SettingsTypes.GetSessionOverageConfigFailure,
    ),
  updateSessionOverageConfig: (body: UpdateSessionOverageConfigRequest) =>
    createGRPCAction<typeof OrgsServicePublic.UpdateSessionOverageConfig>()(
      {
        type: SettingsTypes.UpdateSessionOverageConfigRequest,
        grpcFunction: OrgsServicePublic.UpdateSessionOverageConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.UpdateSessionOverageConfigSuccess,
      SettingsTypes.UpdateSessionOverageConfigFailure,
    ),
  getNotificationConfig: (body: OrgID) =>
    createGRPCAction<typeof OrgsServicePublic.GetNotificationConfig>()(
      {
        type: SettingsTypes.GetNotificationConfigRequest,
        grpcFunction: OrgsServicePublic.GetNotificationConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.GetNotificationConfigSuccess,
      SettingsTypes.GetNotificationConfigFailure,
    ),
  updateNotificationConfig: (body: UpdateNotificationConfigRequest) =>
    createGRPCAction<typeof OrgsServicePublic.UpdateNotificationConfig>()(
      {
        type: SettingsTypes.UpdateNotificationConfigRequest,
        grpcFunction: OrgsServicePublic.UpdateNotificationConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.UpdateNotificationConfigSuccess,
      SettingsTypes.UpdateNotificationConfigFailure,
    ),
  getCustomSeatRequestConfig: (body: OrgID) =>
    createGRPCAction<typeof OrgsServicePublic.GetCustomSeatRequestConfig>()(
      {
        type: SettingsTypes.GetCustomSeatRequestConfigRequest,
        grpcFunction: OrgsServicePublic.GetCustomSeatRequestConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.GetCustomSeatRequestConfigSuccess,
      SettingsTypes.GetCustomSeatRequestConfigFailure,
    ),
  updateCustomSeatRequestConfig: (body: UpdateCustomSeatRequestConfigRequest) =>
    createGRPCAction<typeof OrgsServicePublic.UpdateCustomSeatRequestConfig>()(
      {
        type: SettingsTypes.UpdateCustomSeatRequestConfigRequest,
        grpcFunction: OrgsServicePublic.UpdateCustomSeatRequestConfig,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.UpdateCustomSeatRequestConfigSuccess,
      SettingsTypes.UpdateCustomSeatRequestConfigFailure,
    ),
  updateDismissibleWarning: (body: UpdateDismissibleWarningRequest) =>
    createGRPCAction<typeof SeatSettingsPublic.UpdateDismissibleWarning>()(
      {
        type: SettingsTypes.UpdateDismissibleWarningRequest,
        grpcFunction: SeatSettingsPublic.UpdateDismissibleWarning,
        readWrite: 'write',
        force: true,
        body,
      },
      SettingsTypes.UpdateDismissibleWarningSuccess,
      SettingsTypes.UpdateDismissibleWarningFailure,
    ),
};

export const SettingsBannerActions = {
  snoozeSessionBannerNotification: () =>
    createAction({
      type: SettingsTypes.SnoozeSessionBannerNotification,
    }),
};

export type SettingsActions =
  | RequestActionType<typeof SettingsRequestActions>
  | ActionType<typeof SettingsBannerActions>;

const getApiKeysHashKey = (orgId: string): string =>
  defaultCacheKey(
    AuthPublic.GetApiKeys(() => new Promise<GetApiKeysResponse>(_ => {}), { orgId: '' }).endpoint,
    { orgId },
  );
